import { useState, type ReactNode } from 'react'
import { cn } from '@/lib/cn'
import { Badge } from '@/components/ui'

/** Every step opens with a plain-English explanation of what is being asked. */
export function StepIntro({ title, children, note }: { title: string; children: ReactNode; note?: ReactNode }) {
  return (
    <header className="mb-6">
      <h1 className="text-display">{title}</h1>
      <p className="mt-2 max-w-2xl text-[15px] leading-relaxed text-ink-300">{children}</p>
      {note && <p className="mt-2 max-w-2xl text-sm text-ink-500">{note}</p>}
    </header>
  )
}

export function ChoiceCard({
  selected, onSelect, title, tagline, badges, children, disabled,
}: {
  selected: boolean
  onSelect: () => void
  title: string
  tagline?: string
  badges?: ReactNode
  children?: ReactNode
  disabled?: boolean
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      disabled={disabled}
      aria-pressed={selected}
      className={cn(
        'group relative flex w-full flex-col rounded-[--radius-card] border p-4 text-left transition-colors',
        'disabled:cursor-not-allowed disabled:opacity-40',
        selected
          ? 'border-gold-500 bg-gold-900/30 ring-1 ring-gold-500/40'
          : 'border-ink-700/70 bg-ink-850/70 hover:border-ink-500 hover:bg-ink-800/70',
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <h3 className={cn('font-display text-base leading-tight', selected ? 'text-gold-400' : 'text-ink-100')}>
          {title}
        </h3>
        <span
          aria-hidden
          className={cn(
            'mt-0.5 grid size-5 shrink-0 place-items-center rounded-full border text-[11px] transition-colors',
            selected ? 'border-gold-500 bg-gold-500 text-on-accent' : 'border-ink-600 text-transparent',
          )}
        >
          ✓
        </span>
      </div>

      {tagline && <p className="mt-1.5 text-sm leading-snug text-ink-300">{tagline}</p>}
      {badges && <div className="mt-2.5 flex flex-wrap gap-1.5">{badges}</div>}
      {children && <div className="mt-3 border-t border-ink-700/50 pt-3 text-[13px] leading-relaxed text-ink-400">{children}</div>}
    </button>
  )
}

/** Smaller card for picking several things from a list (skills, spells, feats). */
export function PickChip({
  selected, onToggle, disabled, label, meta, help,
}: {
  selected: boolean
  onToggle: () => void
  disabled?: boolean
  label: string
  meta?: string
  help?: string
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      disabled={disabled && !selected}
      aria-pressed={selected}
      className={cn(
        'flex w-full items-start gap-2.5 rounded-xl border px-3 py-2.5 text-left transition-colors',
        'disabled:cursor-not-allowed disabled:opacity-35',
        selected
          ? 'border-gold-500 bg-gold-900/30'
          : 'border-ink-700/70 bg-ink-850/60 hover:border-ink-500',
      )}
    >
      <span
        aria-hidden
        className={cn(
          'mt-0.5 grid size-4 shrink-0 place-items-center rounded border text-[10px] transition-colors',
          selected ? 'border-gold-500 bg-gold-500 text-on-accent' : 'border-ink-600 text-transparent',
        )}
      >
        ✓
      </span>
      <span className="min-w-0 flex-1">
        <span className="flex items-baseline justify-between gap-2">
          <span className={cn('text-sm', selected ? 'text-gold-400' : 'text-ink-100')}>{label}</span>
          {meta && <span className="shrink-0 text-[10px] uppercase tracking-wider text-ink-500">{meta}</span>}
        </span>
        {help && <span className="mt-0.5 block text-[12px] leading-snug text-ink-400">{help}</span>}
      </span>
    </button>
  )
}

/** "2 of 3 chosen" — always visible so nobody wonders why Next is disabled. */
export function PickCounter({ chosen, total, noun }: { chosen: number; total: number; noun: string }) {
  const done = chosen === total
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-medium uppercase tracking-wider',
        done ? 'border-moss-500/40 bg-moss-900/60 text-moss-500' : 'border-ink-600 bg-ink-800 text-ink-400',
      )}
    >
      {done ? '✓' : `${chosen}/${total}`} {noun}
    </span>
  )
}

/** A collapsible "what does this mean?" for jargon we cannot avoid. */
export function Explainer({ term, children }: { term: string; children: ReactNode }) {
  const [open, setOpen] = useState(false)
  return (
    <div className="rounded-xl border border-ink-700/60 bg-ink-900/50">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        className="flex w-full items-center gap-2 px-3 py-2 text-left text-[13px] text-ink-400 transition-colors hover:text-ink-200"
      >
        <span aria-hidden className={cn('transition-transform', open && 'rotate-90')}>›</span>
        What does <span className="text-gold-400">{term}</span> mean?
      </button>
      {open && <div className="px-3 pb-3 pl-8 text-[13px] leading-relaxed text-ink-300">{children}</div>}
    </div>
  )
}

export function WitchlightBadge() {
  return <Badge tone="arcane">Witchlight</Badge>
}

export function SectionLabel({ children, action }: { children: ReactNode; action?: ReactNode }) {
  return (
    <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
      <h2 className="text-xs font-semibold uppercase tracking-[0.18em] text-ink-400">{children}</h2>
      {action}
    </div>
  )
}
